import { cellHeadWord } from "./dayCells";
import { readPref, writePref, type PrefStore } from "./viewPrefs";

/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * W137 — THE WEEK, MINIMIZED OR EXTENDED (William's third look, 2026-10-02, relayed by Ioannis; `docs/WILLIAM-THIRD-LOOK-
 * 20261002.md`). His words: *"instead of the eye, we need a global button or something of extended and minimized (so just
 * as we have them now as squares). now the extended one, could be for 1,2,4 weeks as well. but the view could be like the
 * phone workout overview of the client, without the phone, just the container."*
 *
 * The rules a screen asks, pure, so `weekView.test.ts` executes them. ONE control for the whole page (Fable R1): it lives in
 * the URL beside the span and the date (`calendarQueryKeeping` keeps it), and it is remembered per viewer in localStorage
 * (try/catch — a private window, a blocked store or a thumbnail capture must still render the page).
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

export type WeekMode = "minimized" | "extended";
/** The two segments, in his order and his words. */
export const WEEK_MODES: readonly WeekMode[] = ["minimized", "extended"];
export const WEEK_MODE_LABEL: Readonly<Record<WeekMode, string>> = { minimized: "Minimized", extended: "Extended" };

/** The URL key (`…?calendarWeekView=2&date=…&weekMode=extended`) — a pasted link opens the same view. */
export const WEEK_MODE_QUERY_KEY = "weekMode";
/** The per-viewer memory. ONE key for the client Week and the Program board: "needs to look same". */
export const WEEK_MODE_STORAGE_KEY = "ht-week-mode";

function parse(raw: unknown): WeekMode | null {
  return raw === "minimized" || raw === "extended" ? raw : null;
}

/**
 * What the page shows: the URL's word when it says one, else what this viewer last chose, else Minimized — today's squares,
 * so nobody's page changes under them until they press Extended.
 * ⚠️ AN UNKNOWN WORD IN EITHER PLACE IS IGNORED, never shown: `?weekMode=wide` is a link from nowhere, not a third mode.
 */
export function weekModeFrom(q: Record<string, string>, stored: string | null): WeekMode {
  /* Week editor stage 1 (2026-10-19, design A): a coach opens the Week on EXTENDED — the week he edits in place. The URL and a
     stored choice still win. */
  return parse(q[WEEK_MODE_QUERY_KEY]) ?? parse(stored) ?? "extended";
}

/** The query with the mode written and every other key KEPT (the calendar's own rule, `calendarQueryKeeping`). */
export function weekModeQueryKeeping(q: Record<string, string>, mode: WeekMode): string {
  const next = new URLSearchParams(q);
  next.set(WEEK_MODE_QUERY_KEY, mode);
  return `?${next.toString()}`;
}

/** The coach's remembered choice (W140: through `viewPrefs`, per coach; `WEEK_MODE_STORAGE_KEY` is its pre-W140 flat key,
 *  read there as a fallback). Absent store, blocked store, private window → null. */
export function readStoredWeekMode(store: Pick<Storage, "getItem"> | undefined): string | null {
  return readPref("weekMode", store);
}
/** …and written without ever throwing. A refused write leaves the URL as the only memory, which still works. */
export function storeWeekMode(store: PrefStore | undefined, mode: WeekMode): void {
  writePref("weekMode", mode, store);
}

/* ── R5b (his word, 2026-10-02 ~10:45: *"or even better have the option to scroll globally so all days, all page as 1, or
 * separately each day. so that can be another button or switch. next to the extended/minimized"*) ─────────────────────── */

export type ScrollMode = "each" | "together";
export const SCROLL_MODES: readonly ScrollMode[] = ["each", "together"];
export const SCROLL_MODE_LABEL: Readonly<Record<ScrollMode, string>> = { each: "Each day", together: "Together" };
export const SCROLL_MODE_QUERY_KEY = "weekScroll";
export const SCROLL_MODE_STORAGE_KEY = "ht-week-scroll";

/** `Each day` is the default — his first words; `Together` is the option he added. Same URL-then-memory rule as the view. */
export function scrollModeFrom(q: Record<string, string>, stored: string | null): ScrollMode {
  const ok = (raw: unknown): ScrollMode | null => (raw === "each" || raw === "together" ? raw : null);
  return ok(q[SCROLL_MODE_QUERY_KEY]) ?? ok(stored) ?? "each";
}
export function scrollModeQueryKeeping(q: Record<string, string>, mode: ScrollMode): string {
  const next = new URLSearchParams(q);
  next.set(SCROLL_MODE_QUERY_KEY, mode);
  return `?${next.toString()}`;
}
export function readStoredScrollMode(store: Pick<Storage, "getItem"> | undefined): string | null {
  return readPref("weekScroll", store);
}
export function storeScrollMode(store: PrefStore | undefined, mode: ScrollMode): void {
  writePref("weekScroll", mode, store);
}

/* ── COACH NOTES: SHOW · HIDE (his word, 2026-10-15, on William's item 3: *"maybe have a hide/show coach notes? on top where's
 * the scroll selection and the others"*). The extended column draws each exercise's coach note under its rest (W137); this
 * switch, beside View and Scroll, takes them off the columns for a coach who wants only the doses. Same URL-then-memory rule. */

export type NotesMode = "show" | "hide";
export const NOTES_MODES: readonly NotesMode[] = ["show", "hide"];
export const NOTES_MODE_LABEL: Readonly<Record<NotesMode, string>> = { show: "Show", hide: "Hide" };
export const NOTES_MODE_QUERY_KEY = "weekNotes";

/** `Show` is the default — the notes are the coach's own words to the client; hiding them is the choice. */
export function notesModeFrom(q: Record<string, string>, stored: string | null): NotesMode {
  const ok = (raw: unknown): NotesMode | null => (raw === "show" || raw === "hide" ? raw : null);
  return ok(q[NOTES_MODE_QUERY_KEY]) ?? ok(stored) ?? "show";
}
export function notesModeQueryKeeping(q: Record<string, string>, mode: NotesMode): string {
  const next = new URLSearchParams(q);
  next.set(NOTES_MODE_QUERY_KEY, mode);
  return `?${next.toString()}`;
}
export function readStoredNotesMode(store: Pick<Storage, "getItem"> | undefined): string | null {
  return readPref("weekNotes", store);
}
export function storeNotesMode(store: PrefStore | undefined, mode: NotesMode): void {
  writePref("weekNotes", mode, store);
}

/** What the page draws, decided ONCE from the four facts that decide it (R5 + R5b). */
export type WeekLayout = {
  /** Square cards (today's), or day columns. */
  kind: "squares" | "columns";
  /** The `Scroll: Each day · Together` control — only in Extended, and never on a narrow screen (one scroll there). */
  scrollControl: boolean;
  /** Each day column is its own vertical scroll container, with its own `↑`. */
  dayScrolls: boolean;
  /** A week row's height when the days scroll: the viewport's remaining height (1 week) or a cap (2/4 weeks). */
  rowHeight: "viewport" | "capped" | "content";
  /** The day heads stick to the top while the PAGE scrolls the week (`Together`). */
  stickyHeads: boolean;
  /** ONE `↑` at the page's bottom-right, back to the top of the week(s) (`Together`). */
  pageTopButton: boolean;
  /** Narrow: one day per row, full width, the page scrolls. */
  oneDayPerRow: boolean;
};

export function weekLayout(f: { mode: WeekMode; scroll: ScrollMode; span: 1 | 2 | 4; narrow: boolean }): WeekLayout {
  if (f.mode === "minimized") {
    return { kind: "squares", scrollControl: false, dayScrolls: false, rowHeight: "content", stickyHeads: false,
             pageTopButton: false, oneDayPerRow: false };
  }
  if (f.narrow) {
    return { kind: "columns", scrollControl: false, dayScrolls: false, rowHeight: "content", stickyHeads: false,
             pageTopButton: false, oneDayPerRow: true };
  }
  if (f.scroll === "together") {
    return { kind: "columns", scrollControl: true, dayScrolls: false, rowHeight: "content", stickyHeads: true,
             pageTopButton: true, oneDayPerRow: false };
  }
  return { kind: "columns", scrollControl: true, dayScrolls: true, rowHeight: f.span === 1 ? "viewport" : "capped",
           stickyHeads: false, pageTopButton: false, oneDayPerRow: false };
}

/** The page's one `↑` in `Together`. */
export const BACK_TO_TOP_OF_WEEK = "Back to top of the week";

/**
 * 🔴 THE `↑` BUTTON'S NAME — one per day column, scrolling THAT column alone (his: *"in all days, we can have a button bottom
 * right to go at the top with 1 click, in each day though separately"*). Named for the day so a screen reader can tell four
 * weeks of them apart.
 */
export function backToTopLabel(dayKey: string): string {
  return `Back to top of ${cellHeadWord(dayKey, 1)}`;
}

/**
 * Drawn only while the column is scrolled away from its top — at its top the button would scroll nothing, which is a
 * control that does nothing. The threshold is a few pixels, so a sub-pixel scroll from a layout change does not flash it.
 */
export const BACK_TO_TOP_AFTER_PX = 8;
export function showsBackToTop(scrollTop: number): boolean {
  return scrollTop > BACK_TO_TOP_AFTER_PX;
}

/** The rest line under an exercise's dose in the extended column: `Rest 1:30`. Null when no set carries a rest. */
export function restLine(rest: string | null): string | null {
  return rest === null ? null : `Rest ${rest}`;
}

/**
 * The coach's note is CLAMPED to three lines in the column and opens on hover or focus (no click needed). A note long enough
 * to be clamped gets a tab stop so the keyboard can open it too; a short one does not, because a tab stop that reveals
 * nothing is a stop for nothing. The bound is a rough three narrow lines of characters — erring LONG costs one extra stop.
 */
export const NOTE_FOCUSABLE_FROM = 80;
export function noteTakesFocus(note: string): boolean {
  return note.length >= NOTE_FOCUSABLE_FROM;
}

/** Every visible day's workout ids, once each, in day order — the key of the ONE batched read (R4). */
export function visibleWorkoutIds(days: readonly { items: readonly { workoutId?: string }[] }[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const d of days) for (const i of d.items) {
    if (i.workoutId === undefined || seen.has(i.workoutId)) continue;
    seen.add(i.workoutId);
    out.push(i.workoutId);
  }
  return out;
}
